import WidgetConfigMenu from './widget-config-menu'
import _ from 'lodash'
import WidgetPeriodSelector from './widget-period-selector'
import DataSourceSelector
	from 'scoreboard/components/data-source-selector/data-source-selector'
import BootslideMenu from '../../components/bootslide/bootslide-menu'
import {
	INITIATIVE,
	SCORECARD_ITEM,
	DASHBOARD
} from 'scoreboard/components/domain-type'
import createConfigWidgetPeriodOption from './create-config-widget-period-option'
import { DATA_SOURCE_PROPERTY } from 'scoreboard/dashboards/widgets/shape/shape-widget-properties'

/**
 * Base class for config menus for widgets that have common "Set Data Source"
 * functionality
 */
const DataSourceWidgetConfigMenu = WidgetConfigMenu.extend({

	/**
	 * Creates a bootslide menu item for setting a widget's data source.
	 * The exact menu will depend on whether or not multiple widgets are selected.
	 * (see #61076)
	 *
	 * @param {Object} options see createFullDataSourceOption()
	 */
	createDataSourceOption (options = {}) {
		let showPeriodSelector = (options.showPeriodSelector ?? true)
			&& ([SCORECARD_ITEM, INITIATIVE].includes(this.model.domainType()))
		let multipleWidgetsSelected =
			this.options.widgetUpdater.canvas.selection.wobs.length > 1

		if (multipleWidgetsSelected	&& showPeriodSelector) {
			// #61076 if multiple widgets are selected and can have a calendar
			// period configured, show just the period selector dialog instead
			// of the full Set Data Source menu
			return createConfigWidgetPeriodOption(this)
		} else {
			return this.createFullDataSourceOption(options)
		}
	},

	/**
	 * Creates a bootslide menu item for setting a widget's data source (#52717).
	 * This applies to many types of widgets, but not all.
	 *
	 * @param {Object} options
	 * @param {WidgetModel} options.model
	 * @param {Dataset} [options.dataset]
	 * @param {WidgetUpdater} options.widgetUpdater
	 * @param {String[]} [options.allowedDomainTypes=[SCORECARD_ITEM, INITIATIVE, DATASET]]
	 * @param {Boolean} options.showDataProperty - true if the dataProperty (ie "Field")
	 * dropdown should be shown
	 * @param {Function|Object[]} options.dataPropertyOptions - the options for the
	 * dataProperty dropdown. Can be an array of objects suitable for a dropdown,
	 * or a function that returns such an array of the domain types that are allowed
	 * to be selected
	 * @param {Object} options.dataSourceSelectorOptions - additional options to pass to
	 * the DataSourceSelector
	 */
	createFullDataSourceOption (options) {
		if (_.isEmpty(options)) {
			options = this.createDataSourceOptionOptions()
		}
		let {
			model,
			widgetUpdater,
			allowedDomainTypes,
			showDataProperty = false,
			dataPropertyOptions,
			dataSourceSelectorOptions,
			showPeriodSelector = true
		} = options
		// #58542 don't provide a period selector to DataSourceSelector if
		// showPeriodSelector is false (DataSourceSelector will not display a
		// period selector if the periodSelector option is not populated)
		let periodSelector
		if (showPeriodSelector) {
			periodSelector = new WidgetPeriodSelector({model})
		}

		const viewCreator = () => {
			let selector = new DataSourceSelector(_.extend({
				domainType: model.domainType?.(),
				dataSource: this.getDataSource(model),
				showDataProperty,
				dataPropertyOptions,
				showNone: true,
				periodSelector,
				allowedDomainTypes,
				datasets: model.data.datasets(),
				scorecards: model.data.scorecards(),
				initiatives: model.data.initiatives(),
				treeItemsById: model.data.datasetTreeItems(),
				parentDomainType: DASHBOARD,
				// https://github.com/SpiderStrategies/Scoreboard/issues/45540#issuecomment-1050099534
				useStartingDataset: false,
				allowAppCalendar: true,
				allowPercentageAggregationType: false,
				datasetErrorCode: model.dataset?.()?.get('errorCode'),
				// #59064 - Do not allow dataset dashboard widgets to include
				// editable fields to prevent an error from occurring
				includeEditFields: false
			}, dataSourceSelectorOptions))

			return selector
		}

		let option = BootslideMenu.targetOption(
			'report.designer.setDataSource', viewCreator, results => {
				this.updateDataSource(model, widgetUpdater, results)
			})

		option.type = 'dialog'
		option.id = 'setDataSource'

		return option
	},

	/**
	 * Given the results of DataSourceSelector, updates the widget properties
	 */
	updateDataSource (model, widgetUpdater, results) {
		let dataset = results.dataset
		if (dataset && !model.data.datasets(dataset.id)) {
			// #34196 the source dataset may have changed, and
			// if it isn't already in our data, we need to add it
			model.data.datasetModel(dataset.id, dataset)
		}

		model.trigger('transaction', () => {
			// This performs several updates, but we want it done in a single
			// "transaction" so that a single undo/redo record is created
			model.setDataSource(results)
		})
		if (model.isDatasetSource()) {
			widgetUpdater.datasetWidgetUpdated(model)
		}
	},

	/**
	 * Converts the widget's properties into a DataSource object that can be
	 * used by the DataSourceSelector.
	 */
	getDataSource (model) {
		let section = model.section()
		if (model.isNoneSource?.()) {
			return { domainType: null }
		} else if (section == 'datasets') {
			let dataSource = _.defaults({
				aggregations: model.aggregations?.(),
				filters: model.filters()
			}, model.get('properties'))

			convertToInt(dataSource, 'startingDatasetId', 'fieldId')

			return dataSource
		} else {
			return {
				domainType: model.domainType?.(),
				domainObjectId: model.properties.get('domainObjectId'),
				dataProperty: model.properties.get(DATA_SOURCE_PROPERTY)
			}
		}
	}

})

/**
 * Converts the given properties in the given object to integers. Necessary
 * for widget properties (all Strings) that are expected to be integers.
 */
function convertToInt (object, ...propertyNames) {
	propertyNames.forEach(name => {
		let val = object[name]
		if (!_.isNil(val)) {
			object[name] = parseInt(val)
		}
	})
}

export default DataSourceWidgetConfigMenu

DataSourceWidgetConfigMenu.__filename__ = 'dashboards/widgets/data-source-widget-config-menu.js'
